/**
 * useAdPerformance — React Query hooks for Meta Ads data
 *
 * Fetches ad accounts, campaigns, ad sets, ad creatives, and performance
 * snapshots from Supabase. All tables are admin-only (RLS enforced).
 *
 * Phase 1.5A: Enhanced with ad-level data, reach/frequency/CPM, quality rankings.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

// ─── Types ──────────────────────────────────────────────────

export interface AdAccount {
  id: string;
  meta_account_id: string;
  meta_account_name: string;
  currency: string;
  timezone: string;
  is_active: boolean;
  last_synced_at: string | null;
  sync_status: string;
  sync_error: string | null;
  created_at: string;
}

export interface AdCampaign {
  id: string;
  account_id: string;
  meta_campaign_id: string;
  meta_campaign_name: string;
  objective: string | null;
  status: string | null;
  daily_budget: number | null;
  lifetime_budget: number | null;
  bid_strategy: string | null;
  buying_type: string | null;
  special_ad_categories: string[] | null;
  vertical: string | null;
  lead_type_ids: string[] | null;
  target_states: string[] | null;
}

export interface AdSet {
  id: string;
  campaign_id: string;
  meta_adset_id: string;
  meta_adset_name: string;
  status: string | null;
  daily_budget: number | null;
  lifetime_budget: number | null;
  optimization_goal: string | null;
  bid_amount: number | null;
  targeting: any;
}

export interface AdCreative {
  id: string;
  adset_id: string;
  campaign_id: string;
  meta_ad_id: string;
  meta_ad_name: string;
  status: string | null;
  creative_type: string | null;
  creative_preview_url: string | null;
  creative_body: string | null;
  creative_title: string | null;
  creative_link_url: string | null;
}

export interface AdPerformanceSnapshot {
  id: string;
  account_id: string;
  meta_campaign_id: string | null;
  meta_adset_id: string | null;
  meta_ad_id: string | null;
  date: string;
  level: string;
  spend: number;
  impressions: number;
  reach: number;
  clicks: number;
  link_clicks: number;
  landing_page_views: number;
  cpm: number | null;
  cpc: number | null;
  ctr: number | null;
  frequency: number | null;
  leads_count: number | null;
  cost_per_lead: number | null;
  quality_ranking: string | null;
  engagement_rate_ranking: string | null;
  conversion_rate_ranking: string | null;
}

export interface AdPerformanceGeo {
  id: string;
  ad_account_id: string;
  campaign_id: string | null;
  meta_campaign_id: string | null;
  date: string;
  region: string;
  state_code: string | null;
  country: string;
  spend: number;
  impressions: number;
  reach: number;
  clicks: number;
  link_clicks: number;
  leads_count: number;
  cost_per_lead: number | null;
  cpm: number | null;
  cpc: number | null;
  ctr: number | null;
}

// ─── Derived metrics ────────────────────────────────────────

export interface AggregatedMetrics {
  totalSpend: number;
  totalImpressions: number;
  totalReach: number;
  totalClicks: number;
  totalLinkClicks: number;
  totalLandingPageViews: number;
  totalLeads: number;
  avgCPL: number;
  avgCPC: number;
  avgCTR: number;
  avgCPM: number;
  avgFrequency: number;
  costPerLinkClick: number;
  // Quality rankings (most recent non-null)
  qualityRanking: string | null;
  engagementRateRanking: string | null;
  conversionRateRanking: string | null;
}

export function aggregateSnapshots(snapshots: AdPerformanceSnapshot[]): AggregatedMetrics {
  const totalSpend = snapshots.reduce((sum, s) => sum + (s.spend || 0), 0);
  const totalImpressions = snapshots.reduce((sum, s) => sum + (s.impressions || 0), 0);
  const totalReach = snapshots.reduce((sum, s) => sum + (s.reach || 0), 0);
  const totalClicks = snapshots.reduce((sum, s) => sum + (s.clicks || 0), 0);
  const totalLinkClicks = snapshots.reduce((sum, s) => sum + (s.link_clicks || 0), 0);
  const totalLandingPageViews = snapshots.reduce((sum, s) => sum + (s.landing_page_views || 0), 0);
  const totalLeads = snapshots.reduce((sum, s) => sum + (s.leads_count || 0), 0);

  // Quality rankings: pick the most recent non-null value
  let qualityRanking: string | null = null;
  let engagementRateRanking: string | null = null;
  let conversionRateRanking: string | null = null;
  // Snapshots are sorted by date DESC, so first non-null is most recent
  for (const s of snapshots) {
    if (!qualityRanking && s.quality_ranking) qualityRanking = s.quality_ranking;
    if (!engagementRateRanking && s.engagement_rate_ranking) engagementRateRanking = s.engagement_rate_ranking;
    if (!conversionRateRanking && s.conversion_rate_ranking) conversionRateRanking = s.conversion_rate_ranking;
    if (qualityRanking && engagementRateRanking && conversionRateRanking) break;
  }

  return {
    totalSpend,
    totalImpressions,
    totalReach,
    totalClicks,
    totalLinkClicks,
    totalLandingPageViews,
    totalLeads,
    avgCPL: totalLeads > 0 ? totalSpend / totalLeads : 0,
    avgCPC: totalClicks > 0 ? totalSpend / totalClicks : 0,
    avgCTR: totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0,
    avgCPM: totalImpressions > 0 ? (totalSpend / totalImpressions) * 1000 : 0,
    avgFrequency: totalReach > 0 ? totalImpressions / totalReach : 0,
    costPerLinkClick: totalLinkClicks > 0 ? totalSpend / totalLinkClicks : 0,
    qualityRanking,
    engagementRateRanking,
    conversionRateRanking,
  };
}

// ─── Snapshot select fields ─────────────────────────────────

const SNAPSHOT_SELECT = "id, account_id, meta_campaign_id, meta_adset_id, meta_ad_id, date, level, spend, impressions, reach, clicks, link_clicks, landing_page_views, cpm, cpc, ctr, frequency, leads_count, cost_per_lead, quality_ranking, engagement_rate_ranking, conversion_rate_ranking";

// ─── Hooks ──────────────────────────────────────────────────

export function useAdAccounts() {
  return useQuery({
    queryKey: ["ad_accounts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ad_accounts")
        .select("id, meta_account_id, meta_account_name, currency, timezone, is_active, last_synced_at, sync_status, sync_error, created_at")
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as AdAccount[];
    },
  });
}

export function useAdCampaigns(accountId?: string) {
  return useQuery({
    queryKey: ["ad_campaigns", accountId],
    queryFn: async () => {
      let query = supabase
        .from("ad_campaigns")
        .select("id, account_id, meta_campaign_id, meta_campaign_name, objective, status, daily_budget, lifetime_budget, bid_strategy, buying_type, special_ad_categories, vertical, lead_type_ids, target_states")
        .order("meta_campaign_name", { ascending: true });
      if (accountId) {
        query = query.eq("account_id", accountId);
      }
      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as AdCampaign[];
    },
  });
}

export function useAdSets(campaignId?: string) {
  return useQuery({
    queryKey: ["ad_sets", campaignId],
    queryFn: async () => {
      let query = supabase
        .from("ad_sets")
        .select("id, campaign_id, meta_adset_id, meta_adset_name, status, daily_budget, lifetime_budget, optimization_goal, bid_amount, targeting")
        .order("meta_adset_name", { ascending: true });
      if (campaignId) {
        query = query.eq("campaign_id", campaignId);
      }
      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as AdSet[];
    },
  });
}

export function useAdCreatives() {
  return useQuery({
    queryKey: ["ad_creatives"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ad_creatives")
        .select("id, adset_id, campaign_id, meta_ad_id, meta_ad_name, status, creative_type, creative_preview_url, creative_body, creative_title, creative_link_url")
        .order("meta_ad_name", { ascending: true });
      if (error) throw error;
      return (data ?? []) as AdCreative[];
    },
  });
}

export function useAdPerformanceSnapshots(options?: {
  accountId?: string;
  campaignId?: string;
  dateFrom?: string;
  dateTo?: string;
  level?: "campaign" | "adset" | "ad";
}) {
  const { accountId, campaignId, dateFrom, dateTo, level } = options ?? {};

  return useQuery({
    queryKey: ["ad_performance_snapshots", accountId, campaignId, dateFrom, dateTo, level],
    queryFn: async () => {
      let query = supabase
        .from("ad_performance_snapshots")
        .select(SNAPSHOT_SELECT)
        .order("date", { ascending: false });

      if (accountId) query = query.eq("account_id", accountId);
      if (campaignId) query = query.eq("meta_campaign_id", campaignId);
      if (dateFrom) query = query.gte("date", dateFrom);
      if (dateTo) query = query.lte("date", dateTo);
      if (level) query = query.eq("level", level);

      const { data, error } = await query.limit(5000);
      if (error) throw error;
      return (data ?? []) as AdPerformanceSnapshot[];
    },
  });
}

/** Fetch all snapshot levels for drill-down (campaign + adset + ad) */
export function useAdPerformanceAllLevels(options?: {
  dateFrom?: string;
  dateTo?: string;
}) {
  const { dateFrom, dateTo } = options ?? {};

  return useQuery({
    queryKey: ["ad_performance_all_levels", dateFrom, dateTo],
    queryFn: async () => {
      let query = supabase
        .from("ad_performance_snapshots")
        .select(SNAPSHOT_SELECT)
        .order("date", { ascending: false });

      if (dateFrom) query = query.gte("date", dateFrom);
      if (dateTo) query = query.lte("date", dateTo);

      const { data, error } = await query.limit(10000);
      if (error) throw error;
      return (data ?? []) as AdPerformanceSnapshot[];
    },
  });
}

const GEO_SELECT = "id, ad_account_id, campaign_id, meta_campaign_id, date, region, state_code, country, spend, impressions, reach, clicks, link_clicks, leads_count, cost_per_lead, cpm, cpc, ctr";

export function useAdPerformanceGeo(options?: {
  dateFrom?: string;
  dateTo?: string;
  campaignId?: string;
}) {
  const { dateFrom, dateTo, campaignId } = options ?? {};

  return useQuery({
    queryKey: ["ad_performance_geo", dateFrom, dateTo, campaignId],
    queryFn: async () => {
      let query = supabase
        .from("ad_performance_geo")
        .select(GEO_SELECT)
        .order("date", { ascending: false });

      if (dateFrom) query = query.gte("date", dateFrom);
      if (dateTo) query = query.lte("date", dateTo);
      if (campaignId) query = query.eq("meta_campaign_id", campaignId);

      const { data, error } = await query.limit(10000);
      if (error) throw error;
      return (data ?? []) as AdPerformanceGeo[];
    },
  });
}

function invalidateAdQueries(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ["ad_accounts"] });
  queryClient.invalidateQueries({ queryKey: ["ad_campaigns"] });
  queryClient.invalidateQueries({ queryKey: ["ad_sets"] });
  queryClient.invalidateQueries({ queryKey: ["ad_creatives"] });
  queryClient.invalidateQueries({ queryKey: ["ad_performance_snapshots"] });
  queryClient.invalidateQueries({ queryKey: ["ad_performance_all_levels"] });
  queryClient.invalidateQueries({ queryKey: ["ad_performance_geo"] });
}

export function useTriggerMetaSync() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("sync-meta-ads", {
        body: { manual: true },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => invalidateAdQueries(queryClient),
  });
}

export function useTriggerBackfill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ since, until }: { since: string; until: string }) => {
      const { data, error } = await supabase.functions.invoke("sync-meta-ads", {
        body: { manual: true, since, until },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => invalidateAdQueries(queryClient),
  });
}
